import {
  secretNameSchema,
  secretValueMaxLength,
  secretValueMinLength,
  type SecretListing,
} from "@repo/app-types/entities/secret";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { CreatableComboboxField } from "@repo/ui/combobox/creatableComboboxField";
import { FieldError } from "@repo/ui/field/fieldError";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import xor from "lodash-es/xor.js";
import { DateTime } from "luxon";
import { useMemo, useState, type FC, type ReactNode } from "react";
import { Dialog } from "react-aria-components";
import {
  useCreateSecretMutation,
  useListSecretsQuery,
  useUpdateSecretMutation,
} from "../../../api/entities/secret.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { applyAllowedDomainsChange } from "../../tools/formConfigs/webDocumentResearchAllowedDomains.js";
import { SecretField } from "./secretField.js";
import { SecretNameSearchField } from "./secretNameSearchField.js";

/** Why the name, the value or the domains entered can't be saved, by field. */
interface SecretEntryErrors {
  name?: string;
  value?: string;
  domains?: string;
}

interface ManageSecretDialogProps {
  /** Set to control the dialog; left out, the surrounding `DialogTrigger` controls it. */
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /** The secret to edit, or `undefined` to add one. */
  secret: SecretListing | undefined;
  /** Whether a secret is added only by creating one, from a typed name, without searching the existing ones. */
  isCreateOnly?: boolean;
  /** Secrets the search leaves out, such as those already listed. */
  excludedSecrets?: readonly SecretListing[];
  /** Domains the domain field suggests, and the new secret starts with. */
  suggestedDomains?: readonly string[];
  /** Shown under the domain field. */
  domainsDescription?: ReactNode;
  /** Checks the domains when the secret is submitted, on top of the dialog's own checks. */
  findDomainsError?: (allowedDomains: readonly string[]) => string | undefined;
  /** Called with the secret selected, created or saved. */
  onSubmit: (secret: SecretListing) => void;
}

export const ManageSecretDialog: FC<ManageSecretDialogProps> = (props) => {
  const { isOpen, onOpenChange, ...contentProps } = props;

  return (
    <CenteredModal isDismissable isOpen={isOpen} onOpenChange={onOpenChange}>
      {/* As wide as the modal allows, so its content never resizes it. */}
      <Dialog className="w-dvw max-w-lg px-4 pt-5 pb-4 text-left outline-hidden sm:p-6">
        {({ close }) => (
          <ManageSecretDialogContent {...contentProps} onClose={close} />
        )}
      </Dialog>
    </CenteredModal>
  );
};

const noSecrets: readonly SecretListing[] = [];
const noDomains: readonly string[] = [];

interface ManageSecretDialogContentProps extends Omit<
  ManageSecretDialogProps,
  "isOpen" | "onOpenChange"
> {
  onClose: () => void;
}

/**
 * Adds a secret, by selecting an existing one or creating one, or edits a
 * secret's value and domains. Creating and editing save the secret at once.
 */
const ManageSecretDialogContent: FC<ManageSecretDialogContentProps> = (
  props,
) => {
  const {
    secret,
    isCreateOnly = false,
    excludedSecrets = noSecrets,
    suggestedDomains = noDomains,
    domainsDescription,
    findDomainsError,
    onSubmit,
    onClose,
  } = props;

  // Adding a secret starts with the search, unless it can only be created.
  const [isSearching, setIsSearching] = useState(
    secret === undefined && !isCreateOnly,
  );
  /** A new secret's name: typed, or from the search's "New" option. */
  const [name, setName] = useState("");
  // A value entered here, never one read back.
  const [value, setValue] = useState("");
  const [allowedDomains, setAllowedDomains] = useState<readonly string[]>(
    secret?.allowed_domains ?? suggestedDomains,
  );
  const [errors, setErrors] = useState<SecretEntryErrors>();
  const [saveError, setSaveError] = useState<string>();

  const [createSecret, { isLoading: isCreating }] = useCreateSecretMutation();
  const [updateSecret, { isLoading: isUpdating }] = useUpdateSecretMutation();

  // The same list the search and the Developers page load, so it's usually cached.
  const { data: secrets = noSecrets } = useListSecretsQuery();
  // The suggested domains first, then every domain the company's secrets allow.
  const domainSuggestions = useMemo(() => {
    const domains = new Set([
      ...suggestedDomains,
      ...secrets.flatMap(({ allowed_domains }) => allowed_domains),
    ]);
    return [...domains].filter((domain) => !allowedDomains.includes(domain));
  }, [suggestedDomains, secrets, allowedDomains]);

  const handleNameChange = (next: string) => {
    setName(next);
    setErrors(undefined);
  };

  const findEntryErrors = (): SecretEntryErrors | undefined => {
    const nameError = secret ? undefined : findSecretNameError(name);
    if (nameError) {
      return { name: nameError };
    }
    const valueError = findSecretValueError(value);
    if (valueError) {
      return { value: valueError };
    }
    const domainsError = findDomainsError?.(allowedDomains);
    if (domainsError) {
      return { domains: domainsError };
    }
    return undefined;
  };

  const handleSubmit = async () => {
    setSaveError(undefined);
    // Left empty, an edited secret keeps its value, and so its domains: the
    // API changes a secret's domains only together with its value.
    if (secret && value === "") {
      if (xor(allowedDomains, secret.allowed_domains).length > 0) {
        setErrors({ value: "To edit a secret, you must provide the value." });
      } else {
        onClose();
      }
      return;
    }
    const entryErrors = findEntryErrors();
    if (entryErrors) {
      setErrors(entryErrors);
      return;
    }
    const payload = { value, allowed_domains: [...allowedDomains] };
    try {
      const saved = secret
        ? await updateSecret({ secretId: secret.id, payload }).unwrap()
        : await createSecret({ ...payload, name }).unwrap();
      onSubmit(saved);
      onClose();
    } catch (error) {
      setSaveError(
        getApiErrorMessage(error, "Failed to save the secret. Try again."),
      );
    }
  };

  const handleDomainsChange = (next: readonly string[]) => {
    const change = applyAllowedDomainsChange(allowedDomains, next);
    setAllowedDomains(change.allowedDomains);
    setErrors(
      change.allowedDomainsError
        ? { domains: change.allowedDomainsError }
        : undefined,
    );
  };

  const handleValueChange = (next: string) => {
    setValue(next);
    setErrors(undefined);
  };

  return (
    <>
      <ModalHeader isCloseVisible>
        {secret ? "Edit secret" : "Add secret"}
      </ModalHeader>
      {secret ? (
        <p className="mt-2 text-sm text-muted-foreground">
          Changes apply everywhere this secret is used. To edit a secret, the
          original value must be re-entered.
        </p>
      ) : null}
      <div className="mt-5 space-y-4">
        {isSearching ? (
          <SecretNameSearchField
            onSelect={(selected) => {
              onSubmit(selected);
              onClose();
            }}
            // The new secret's name moves into the secret field, where its
            // value is entered.
            onCreate={(created) => {
              setName(created);
              setIsSearching(false);
            }}
            excludedSecrets={excludedSecrets}
          />
        ) : (
          <>
            <SecretField
              secret={secret}
              name={name}
              onNameChange={handleNameChange}
              value={value}
              onValueChange={handleValueChange}
              nameError={errors?.name}
              valueError={errors?.value}
            />
            <CreatableComboboxField
              label="Allowed domains"
              placeholder="Add a domain (e.g. api.example.com)"
              value={allowedDomains}
              onChange={handleDomainsChange}
              suggestions={domainSuggestions}
              description={domainsDescription}
              errorMessage={errors?.domains}
            />
          </>
        )}

        {saveError ? <FieldError>{saveError}</FieldError> : null}
      </div>

      {isSearching ? null : (
        <div className="mt-6 flex items-center gap-2">
          {secret ? (
            <p className="text-xs text-muted-foreground">
              Last updated{" "}
              {DateTime.fromISO(secret.updated_at).toLocaleString(
                DateTime.DATETIME_MED,
              )}
            </p>
          ) : null}
          <Button
            variant={ButtonVariant.Secondary}
            className="ml-auto"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            onClick={handleSubmit}
            isLoading={isCreating || isUpdating}
          >
            {secret ? "Save" : "Create"}
          </Button>
        </div>
      )}
    </>
  );
};

/** Why a new secret's name can't be used, or `undefined` when it can. */
function findSecretNameError(name: string): string | undefined {
  const nameResult = secretNameSchema.safeParse(name);
  return nameResult.success ? undefined : nameResult.error.issues[0]?.message;
}

/** Why a value can't be saved, or `undefined` when it can. */
function findSecretValueError(value: string): string | undefined {
  if (
    value.length < secretValueMinLength ||
    value.length > secretValueMaxLength
  ) {
    return `Enter a value of ${secretValueMinLength.toString()} to ${secretValueMaxLength.toLocaleString()} characters.`;
  }
  return undefined;
}
